import { useEffect, useState } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useUser } from './useUser';

export type EmailScope = 'aosenuma' | 'cliente';
export type EmailStatus = 'active' | 'suspended';
export type EmailRol = 'admin_aosenuma' | 'tecnico_aosenuma' | 'auditor' | 'cliente_elawan';

export interface AllowedEmailRow {
  email: string;
  scope: EmailScope;
  tenant_slug: string | null;
  status: EmailStatus;
  rol_app: EmailRol;
  notas: string | null;
  /** Si false, el correo entra sin TOTP (aal1). Super-admins ignoran el flag. */
  totp_required: boolean;
  /** Si false, no puede revelar PII (BFF bloquea unmask, UI oculta el ojo). */
  pii_access: boolean;
}

interface UseAllowedEmailResult {
  /** null mientras carga; row si está autorizado; undefined si no aparece en la whitelist. */
  row: AllowedEmailRow | null | undefined;
  loading: boolean;
  error: Error | null;
}

/**
 * Lee el row de `public.allowed_emails` del usuario actual. Tres estados:
 *   - row = null    → cargando o sin sesión
 *   - row = undefined → autenticado pero NO en la whitelist (forbidden)
 *   - row = AllowedEmailRow → autorizado (verificar status='active')
 *
 * RLS permite `SELECT` solo de la fila propia (policy allowed_emails_self_read),
 * por eso el hook funciona con el JWT del user actual sin necesidad de BFF.
 */
export function useAllowedEmail(): UseAllowedEmailResult {
  const { user } = useUser();
  const [row, setRow] = useState<AllowedEmailRow | null | undefined>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user?.email || !isSupabaseConfigured()) {
      setRow(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const sb = getSupabase();
        const { data, error: qErr } = await sb
          .from('allowed_emails')
          .select('email, scope, tenant_slug, status, rol_app, notas, totp_required, pii_access')
          .eq('email', user.email!.toLowerCase().trim())
          .maybeSingle();
        if (cancelled) return;
        if (qErr) {
          setError(new Error(qErr.message));
          setRow(null);
        } else {
          // null → no row encontrada = NO autorizado (lo modelamos como `undefined`).
          setRow((data as AllowedEmailRow | null) ?? undefined);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setRow(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  return { row, loading, error };
}

const SUPER_ADMINS: ReadonlySet<string> = new Set([
  'diegotec13@hotmail.com',
  'arriaga.marts@gmail.com',
  'javsmk1014@gmail.com',
]);

/**
 * ¿El correo dado es uno de los 3 super-admins? Lista hardcoded idéntica a
 * `is_super_admin()`/`is_super_admin_email()` en DB. Útil para bloquear en UI
 * acciones vetadas a super-admins (p. ej. desactivarles TOTP). La autoridad
 * real es server-side (RPC `allowed_email_set_flags` rechaza el caso).
 */
export function isSuperAdminEmail(email: string | null | undefined): boolean {
  const e = email?.toLowerCase().trim();
  return !!e && SUPER_ADMINS.has(e);
}

/**
 * Hook trivial: ¿el user actual es uno de los 3 super-admins hardcoded?
 *
 * Hardcoded a propósito — la BD tiene la misma lista en `is_super_admin()`.
 * Tener la verificación FE evita roundtrips para mostrar/ocultar UI; las
 * acciones que realmente importan están protegidas por RLS server-side.
 */
export function useIsSuperAdmin(): boolean {
  const { user } = useUser();
  return isSuperAdminEmail(user?.email);
}
